import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

export type SegmentedFilterTone = "primary" | "danger" | "warning" | "neutral";

export type SegmentedFilterOption<T extends string = string> = {
  value: T;
  label: string;
  count?: number | null;
  /** If true, do not display (0) when count is 0 */
  hideZeroCount?: boolean;
  icon?: ReactNode;
  /** Tone for the count number when unselected (default neutral) */
  tone?: SegmentedFilterTone;
  disabled?: boolean;
};

export type SegmentedFilterProps<T extends string = string> = {
  options: SegmentedFilterOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  size?: "sm" | "md";
  ariaLabel?: string;
  /** Allow horizontal slide/scroll and prevent text truncation for longer labels */
  scrollable?: boolean;
  /** Size each button to its label and wrap onto the next line. */
  wrap?: boolean;
};

/**
 * Mobile-First Segmented Filter Control.
 * Used for switching sub-views/queues (e.g. All vs. Unread vs. Urgent).
 */
export function SegmentedFilter<T extends string = string>({
  options,
  value,
  onChange,
  className,
  size = "md",
  ariaLabel = "Bộ lọc danh sách",
  scrollable = false,
  wrap = false,
}: SegmentedFilterProps<T>) {
  if (options.length === 0) return null;
  const fitLabel = wrap || scrollable;

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        "rounded-xl border border-border/80 bg-surface-raised p-1 shadow-xs",
        wrap
          ? "flex w-full flex-wrap gap-1.5"
          : scrollable
            ? "flex w-full items-center gap-1.5 overflow-x-auto scrollbar-none overscroll-x-contain"
            : "grid w-full",
        className,
      )}
      style={
        fitLabel
          ? undefined
          : {
              gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))`,
            }
      }
    >
      {options.map((opt) => {
        const active = opt.value === value;
        const showCount =
          opt.count != null && (!opt.hideZeroCount || opt.count > 0);

        let countTone = "text-text-muted";
        if (active) {
          countTone = "text-white/90 font-semibold";
        } else if (opt.tone === "danger" && (opt.count ?? 0) > 0) {
          countTone = "text-danger font-bold";
        } else if (opt.tone === "warning" && (opt.count ?? 0) > 0) {
          countTone = "text-warning font-bold";
        } else if (opt.tone === "primary" && (opt.count ?? 0) > 0) {
          countTone = "text-primary font-bold";
        }

        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={opt.disabled}
            onClick={(e) => {
              onChange(opt.value);
              if (scrollable && !wrap) {
                e.currentTarget.scrollIntoView({
                  behavior: "smooth",
                  block: "nearest",
                  inline: "center",
                });
              }
            }}
            className={cn(
              "flex items-center justify-center rounded-lg select-none cursor-pointer transition-all",
              fitLabel ? "shrink-0 whitespace-nowrap px-3.5" : "px-1.5",
              size === "sm" ? "py-1.5 text-xs" : "py-2 text-caption",
              active
                ? "bg-primary text-white shadow-xs font-bold"
                : "text-text-secondary hover:text-text hover:bg-surface/50 font-medium",
              opt.disabled && "opacity-50 cursor-not-allowed pointer-events-none",
            )}
          >
            {opt.icon && <span className="mr-1.5 shrink-0" aria-hidden>{opt.icon}</span>}
            <span className={fitLabel ? "whitespace-nowrap" : "truncate"}>{opt.label}</span>
            {showCount && (
              <span className={cn("ml-1.5 shrink-0 text-xs tabular-nums", countTone)}>
                ({opt.count})
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
